<template>
    <section name="location menu" class="gap-md px-sm py-md flex flex-col">
        <div class="input-wrapper pr-2.5">
            <input v-model="search" type="search" placeholder="Search" autocomplete="off" />
            <i class="i-search" />
        </div>
        <ul class="gap-xs flex flex-col">
            <LocationNode
                v-for="location in filteredLocations"
                :key="location.id"
                :node="location"
                :search="search"
                :is-expanded="isExpanded"
                :is-active="isActive"
                :toggle="toggle"
                :is-visible="isVisible"
                :multiple="multiple"
                :is-selected="isSelected"
                :toggle-selection="toggleSelection"
            />
        </ul>
    </section>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted, watch } from 'vue';
import LocationNode from '@/components/admin/infrastructure/location-node.vue';
import type { UUIDLocation } from '@/stores/store-interfaces';

const props = defineProps<{
    locations: UUIDLocation[];
    activeLocation?: UUIDLocation | null;
    multiple?: boolean;
    selectedIds?: string[];
}>();

const search = ref('');
const expanded = reactive(new Set<string>());
const visible = reactive(new Set<string>());
const emit = defineEmits(['updateLocation', 'update:selectedIds']);

// Handle toggle functionality
function toggle(e: Event, item: UUIDLocation) {
    e.preventDefault();
    const id = item.id;
    const needsOpening = !expanded.has(id);

    if (needsOpening) {
        expanded.add(id);
    } else {
        expanded.delete(id);
    }
    
    if (!props.multiple) {
        emit('updateLocation', item);
    }
}

function toggleSelection(e: Event, item: UUIDLocation) {
     e.preventDefault();
     if (!props.multiple) return;

     const currentSelection = new Set(props.selectedIds || []);
     if (currentSelection.has(item.id)) {
         currentSelection.delete(item.id);
     } else {
         currentSelection.add(item.id);
     }
     emit('update:selectedIds', Array.from(currentSelection));
}

// Check if an item is expanded
function isExpanded(item: UUIDLocation): boolean {
    return expanded.has(item.id);
}

function isSelected(item: UUIDLocation): boolean {
    return props.selectedIds?.includes(item.id) ?? false;
}

function isActive(item: UUIDLocation): boolean {
    if (props.multiple) return false; // In multiple mode, we use checkboxes/isSelected, not active state highlight
    
    const isActiveNode = item.id === props.activeLocation?.id;

    const hasActiveChild = (node: UUIDLocation): boolean => {
        return node.children?.some((child) => child.id === props.activeLocation?.id || hasActiveChild(child)) || false;
    };

    return isActiveNode || hasActiveChild(item);
}

function isVisible(item: UUIDLocation): boolean {
    return visible.has(item.id);
}

// Expand only level 0 (top-level) nodes on mount
onMounted(() => {
    props.locations.forEach((loc) => expand(loc));
});

function expand(item: UUIDLocation) {
    const id = item.id;
    if (!expanded.has(id)) {
        expanded.add(id);
    }
}

// Filter tree, always return full tree, but mark nodes as visible based on search
const filteredLocations = computed(() => {
    visible.clear(); // Clear previous state

    interface VisibilityResult {
        node: UUIDLocation;
        isDirectMatch: boolean;
        hasDirectMatchDescendant: boolean;
    }

    const markVisibility = (
        node: UUIDLocation,
        searchTerm: string,
        ancestors: string[] = [],
        parentMatches = false
    ): VisibilityResult => {
        const isMatch = node.name.toLowerCase().includes(searchTerm.toLowerCase());
        // If parent matched, this node inherits visibility (it's a child of a match)
        const effectiveMatch = isMatch || parentMatches;

        let hasDirectMatchDescendant = false;

        const children =
            node.children?.map((child) => {
                const result = markVisibility(child, searchTerm, [...ancestors, node.id], effectiveMatch);
                if (result.hasDirectMatchDescendant || result.isDirectMatch) {
                    hasDirectMatchDescendant = true;
                }
                return result.node;
            }) || [];

        // Visibility Logic:
        // 1. I am a match (`isMatch`).
        // 2. I am an ancestor of a match (`hasDirectMatchDescendant`).
        // 3. I am a descendant of a match (`parentMatches`).
        const isVisible = isMatch || hasDirectMatchDescendant || parentMatches;

        if (isVisible) {
            visible.add(node.id);
        }

        if (searchTerm.length > 0) {
            // Expansion Logic:

            // 1. Ancestors must be expanded if I am a match or I lead to a match.
            if (isMatch || hasDirectMatchDescendant) {
                ancestors.forEach((id) => expanded.add(id));
            }
        }

        return {
            node: {
                ...node,
                children,
            },
            isDirectMatch: isMatch,
            hasDirectMatchDescendant,
        };
    };

    return props.locations.map((loc) => markVisibility(loc, search.value).node);
});

watch(
    () => search.value,
    (newValue) => {
        if (newValue.length === 0) {
            expanded.clear();
            visible.clear();
            expanded.add(props.locations[0].id);
        }
    },
);

// Transition styles for fade-list
</script>
